import { motion } from "framer-motion";
import { Smartphone, MoveDiagonal, Film, Brain } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 29 — Future pipeline · what comes next.
 * Four icons map out the upcoming workstreams beyond the current cost-saving win.
 */

const pipeline = [
  {
    icon: Smartphone,
    label: "Capture",
    note: "iPhone-led capture pipeline",
  },
  {
    icon: MoveDiagonal,
    label: "Stabilisation",
    note: "AI-driven motion correction",
  },
  {
    icon: Film,
    label: "Long form",
    note: "Extended walk-throughs · YouTube-ready",
  },
  {
    icon: Brain,
    label: "Room intelligence",
    note: "Auto-tag · auto-segment · auto-caption",
  },
];

export default function Scene29_Foundation() {
  return (
    <SceneShell variant="forest" align="left" data-testid="scene-foundation">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 25% 50%, rgba(150,193,31,0.18) 0%, transparent 55%)",
            "radial-gradient(ellipse at 75% 50%, rgba(0,222,182,0.18) 0%, transparent 55%)",
            "radial-gradient(ellipse at 25% 50%, rgba(150,193,31,0.18) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mb-6"
        >
          What comes next
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-4xl sm:text-5xl lg:text-6xl text-white"
          data-testid="foundation-title"
        >
          Future{" "}
          <span
            style={{
              background:
                "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              fontWeight: 900,
            }}
          >
            pipeline.
          </span>
        </motion.h2>

        {/* Four icon tiles */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {pipeline.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.9,
                  delay: 0.7 + i * 0.18,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="rounded-2xl p-6 relative overflow-hidden"
                style={{
                  background: "rgba(150,193,31,0.06)",
                  border: "1px solid rgba(150,193,31,0.30)",
                }}
                data-testid={`pipeline-${i}`}
              >
                <div
                  className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-25 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(150,193,31,0.5) 0%, transparent 70%)",
                    filter: "blur(20px)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-5">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{
                        background: "rgba(150,193,31,0.18)",
                        color: "#96C11F",
                        border: "1px solid rgba(150,193,31,0.45)",
                      }}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <span
                      className="text-[10px] tracking-[0.32em] uppercase tnum"
                      style={{ color: "#96C11F", fontWeight: 700 }}
                    >
                      0{i + 1}
                    </span>
                  </div>
                  <div
                    className="kn-display text-xl sm:text-2xl mb-2"
                    style={{ color: "#e2e8f0", fontWeight: 700 }}
                  >
                    {p.label}
                  </div>
                  <div
                    className="text-sm"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    {p.note}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </SceneShell>
  );
}
